import React, { useState, useEffect } from 'react';
import { Banknote, Clock, ArrowRightCircle, CheckCircle, X, AlertCircle, RefreshCw } from 'lucide-react';
import { getAdminWithdrawalRequests, approveWithdrawalRequest, completeWithdrawalRequest } from '../../lib/admin';
import type { WithdrawalRequest } from '../../types/admin';

export default function WithdrawalManagement() {
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [completingRequest, setCompletingRequest] = useState<WithdrawalRequest | null>(null);
  const [transferRef, setTransferRef] = useState('');
  const [closeRegistry, setCloseRegistry] = useState(true);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAdminWithdrawalRequests();
      setRequests(data);
    } catch (err) {
      console.error('Error loading withdrawal requests:', err);
      setError('Failed to load withdrawal requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const handleApprove = async (requestId: string) => {
    try {
      setActionLoading(requestId);
      await approveWithdrawalRequest(requestId);
      await loadRequests();
    } catch (err) {
      console.error('Error approving request:', err);
      setError('Failed to approve withdrawal request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async () => {
    if (!completingRequest) return;
    if (!transferRef.trim()) {
      setError('Please enter a transfer reference number');
      return;
    }
    try {
      setActionLoading(completingRequest.id);
      await completeWithdrawalRequest(completingRequest.id, transferRef.trim(), closeRegistry);
      setCompletingRequest(null);
      setTransferRef('');
      setCloseRegistry(true);
      await loadRequests();
    } catch (err) {
      console.error('Error completing request:', err);
      setError('Failed to complete withdrawal request');
    } finally {
      setActionLoading(null);
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-600',
      approved: 'bg-pastel-blue-100 text-pastel-blue-600',
      transferred: 'bg-pastel-green-100 text-pastel-green-600',
      rejected: 'bg-red-100 text-red-600',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-purple-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 font-poppins">
          <Banknote className="w-5 h-5 text-pastel-green-600" />
          Withdrawal Requests
        </h2>
        <button
          onClick={loadRequests}
          className="flex items-center space-x-2 px-3 py-1.5 bg-white/60 text-gray-800 rounded-md hover:bg-white/80 text-sm border border-white/40"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-md flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {requests.length === 0 ? (
        <div className="card-pastel p-8 text-center">
          <Banknote className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No withdrawal requests yet</p>
          <p className="text-sm text-gray-400 mt-1">When registry owners request a withdrawal, it will appear here for approval.</p>
        </div>
      ) : (
        <div className="card-pastel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-white/60">
                <tr>
                  <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Registry</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Owner</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Bank Details</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-pastel-purple-200">
                {requests.map((req) => (
                  <tr key={req.id} className="hover:bg-white/60">
                    <td className="px-4 py-4">
                      <p className="font-medium text-gray-800">{req.registry_title}</p>
                      <p className="text-xs text-gray-500">{new Date(req.requested_at).toLocaleDateString()}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-gray-800">{req.owner_name || 'Unknown'}</p>
                      <p className="text-xs text-gray-500">{req.owner_email}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm font-bold text-pastel-green-600">LKR {Number(req.amount).toFixed(2)}</p>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-xs text-gray-600 space-y-0.5">
                        <p>{req.account_holder_name || 'N/A'}</p>
                        <p>{req.account_number ? `****${req.account_number.slice(-4)}` : 'N/A'}</p>
                        <p>{req.bank_name || 'N/A'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        {statusBadge(req.status)}
                        {req.transfer_reference && (
                          <p className="text-xs text-gray-400">Ref: {req.transfer_reference}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {req.status === 'pending' && (
                          <button
                            onClick={() => handleApprove(req.id)}
                            disabled={actionLoading === req.id}
                            className="flex items-center gap-1 px-3 py-1 bg-pastel-blue-400 text-white rounded-md hover:bg-pastel-blue-500 text-sm disabled:opacity-50"
                          >
                            {actionLoading === req.id ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                            ) : (
                              <ArrowRightCircle className="w-4 h-4" />
                            )}
                            <span>Approve</span>
                          </button>
                        )}
                        {req.status === 'approved' && (
                          <button
                            onClick={() => {
                              setCompletingRequest(req);
                              setTransferRef('');
                              setCloseRegistry(true);
                            }}
                            disabled={actionLoading === req.id}
                            className="flex items-center gap-1 px-3 py-1 bg-pastel-green-400 text-white rounded-md hover:bg-pastel-green-500 text-sm disabled:opacity-50"
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>Complete</span>
                          </button>
                        )}
                        {req.status === 'transferred' && (
                          <span className="text-xs text-gray-400">
                            {req.transferred_at ? new Date(req.transferred_at).toLocaleDateString() : ''}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Complete Transfer Modal */}
      {completingRequest && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50">
          <div className="card-pastel p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 font-poppins">
                <CheckCircle className="w-5 h-5 text-pastel-green-600" />
                Complete Transfer
              </h3>
              <button
                onClick={() => setCompletingRequest(null)}
                className="p-2 hover:bg-white/60 rounded-full transition-colors text-gray-500 hover:text-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 bg-white/60 rounded-lg p-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Registry:</span>
                <span className="text-gray-800">{completingRequest.registry_title}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Owner:</span>
                <span className="text-gray-800">{completingRequest.owner_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Amount:</span>
                <span className="text-pastel-green-600 font-medium">LKR {Number(completingRequest.amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Bank:</span>
                <span className="text-gray-800">{completingRequest.bank_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Account:</span>
                <span className="text-gray-800">{completingRequest.account_holder_name || 'N/A'}</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Transfer Reference Number
                </label>
                <input
                  type="text"
                  value={transferRef}
                  onChange={(e) => setTransferRef(e.target.value)}
                  placeholder="Enter bank transfer reference"
                  className="input-pastel"
                />
                <p className="text-xs text-gray-400 mt-1">Enter the bank reference number for this transfer.</p>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={closeRegistry}
                  onChange={(e) => setCloseRegistry(e.target.checked)}
                  className="w-4 h-4 rounded border-pastel-purple-200 bg-white/60 text-pastel-purple-600 focus:ring-pastel-purple-400"
                />
                <span className="text-sm text-gray-600">Close registry after transfer (mark as fully settled)</span>
              </label>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  onClick={() => setCompletingRequest(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleComplete}
                  disabled={actionLoading === completingRequest.id || !transferRef.trim()}
                  className="px-6 py-2 bg-pastel-green-400 text-white rounded-md hover:bg-pastel-green-500 disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading === completingRequest.id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  <span>Confirm Transfer</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
